import { API_ENDPOINTS } from '../config/api';

/**
 * Fetch all Umrah packages from the backend
 */
export const fetchAllPackages = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.PACKAGES, {
      credentials: 'include', // Include cookies for authentication
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch packages');
    }
    
    return data.data || [];
  } catch (error) {
    console.error('Error fetching packages:', error);
    throw error;
  }
};

/**
 * Fetch package by ID
 */
export const fetchPackageById = async (id) => {
  try {
    const response = await fetch(API_ENDPOINTS.PACKAGE_BY_ID(id), {
      credentials: 'include', // Include cookies for authentication
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch package');
    }
    
    return data.data;
  } catch (error) {
    console.error('Error fetching package:', error);
    throw error;
  }
};

/**
 * Create a new Umrah package
 */
export const createPackage = async (packageData) => {
  try {
    const formData = new FormData();
    
    // Add all fields to FormData
    Object.keys(packageData).forEach(key => {
      // Skip imagePreview - it's only for UI preview, not for backend
      if (key === 'imagePreview' || key === 'monthSelectorValue') {
        return;
      }
      
      if (key === 'packageImage') {
        // Only append if it's actually a File object
        if (packageData[key] instanceof File) {
          formData.append('packageImage', packageData[key]);
        }
        // Silently skip if undefined or not a File (no image selected or already uploaded)
        return;
      } else if (key === 'departureCities' && Array.isArray(packageData[key])) {
        // Convert array to JSON string for FormData
        formData.append(key, JSON.stringify(packageData[key]));
      } else if (packageData[key] !== null && packageData[key] !== undefined) {
        formData.append(key, packageData[key]);
      }
    });
    
    const response = await fetch(API_ENDPOINTS.PACKAGES, {
      method: 'POST',
      credentials: 'include', // Include cookies for authentication
      body: formData, // Don't set Content-Type header, browser will set it with boundary
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create package');
    }
    
    return data.data;
  } catch (error) {
    console.error('Error creating package:', error);
    throw error;
  }
};

/**
 * Update an existing Umrah package
 */
export const updatePackage = async (id, packageData) => {
  try {
    const formData = new FormData();
    
    // Add all fields to FormData
    Object.keys(packageData).forEach(key => {
      // Skip imagePreview - it's only for UI preview, not for backend
      if (key === 'imagePreview' || key === 'monthSelectorValue') {
        return;
      }
      
      if (key === 'packageImage') {
        // Only append if it's actually a File object
        if (packageData[key] instanceof File) {
          formData.append('packageImage', packageData[key]);
        }
        // Silently skip if undefined or not a File (no image selected or already uploaded)
        return;
      } else if (key === 'departureCities' && Array.isArray(packageData[key])) {
        // Convert array to JSON string for FormData
        formData.append(key, JSON.stringify(packageData[key]));
      } else if (packageData[key] !== null && packageData[key] !== undefined) {
        formData.append(key, packageData[key]);
      }
    });
    
    const response = await fetch(API_ENDPOINTS.PACKAGE_BY_ID(id), {
      method: 'PUT',
      credentials: 'include', // Include cookies for authentication
      body: formData, // Don't set Content-Type header, browser will set it with boundary
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to update package');
    }
    
    return data.data;
  } catch (error) {
    console.error('Error updating package:', error);
    throw error;
  }
};

/**
 * Delete an Umrah package
 */
export const deletePackage = async (id) => {
  try {
    const response = await fetch(API_ENDPOINTS.PACKAGE_BY_ID(id), {
      method: 'DELETE',
      credentials: 'include', // Include cookies for authentication
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete package');
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting package:', error);
    throw error;
  }
};

/**
 * Fetch bookings by package ID
 */
export const fetchBookingsByPackageId = async (packageId) => {
  try {
    const response = await fetch(API_ENDPOINTS.PACKAGE_BOOKINGS(packageId), {
      credentials: 'include', // Include cookies for authentication
    });
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch bookings');
    }
    
    return data.data || [];
  } catch (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }
};
